import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import {
  Megaphone, TrendingUp, Eye, MousePointerClick, ShieldCheck, Zap,
  ArrowRight, CheckCircle2, BarChart3, Sparkles, Loader2,
} from "lucide-react";

export default function AdvertisePage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState([]);

  useEffect(() => {
    apiClient.get("/advertiser/pricing").then(({ data }) => setPricing(data.items || []));
  }, []);

  const handleStart = () => {
    if (user) navigate("/advertiser/campaigns/new");
    else navigate("/login", { state: { from: "/advertiser/campaigns/new" } });
  };

  return (
    <div data-testid="advertise-page" className="bg-[#f1ecdf]">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="overline overline-gold mb-4">{lang === "ar" ? "أعلِن على كارتال" : "Advertise on CARTAL"}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] font-medium leading-tight mb-6 max-w-3xl">
          {lang === "ar" ? "اوصل لـ ملايين الباحثين عن سيارة في الشرق الأوسط" : "Reach millions of car buyers across the Middle East"}
        </h1>
        <p className="text-lg text-[#3a3a3a] max-w-2xl mb-10 leading-relaxed">
          {lang === "ar"
            ? "بنرات إعلانية مدفوعة على الواجهة الأولى للسوق العربي للسيارات. أنشئ حملتك في 3 دقائق وادفع بالبطاقة مباشرة."
            : "Premium banner placements on the leading automotive marketplace in the Arabic-speaking Middle East. Launch in 3 minutes — pay by card."}
        </p>
        <div className="flex gap-3 flex-wrap">
          <button onClick={handleStart} className="btn-primary text-base" data-testid="advertise-cta">
            <Zap className="w-4 h-4" /> {lang === "ar" ? "ابدأ حملتك الآن" : "Launch your campaign"} <ArrowRight className="w-4 h-4" />
          </button>
          <a href="#pricing" className="btn-ghost text-base">{lang === "ar" ? "شاهد الأسعار" : "See pricing"}</a>
        </div>

        {/* Why us */}
        <div className="grid sm:grid-cols-3 gap-4 mt-20">
          {[
            { icon: Eye, ar: "+2 مليون ظهور شهري", en: "+2M monthly impressions", sub: lang === "ar" ? "تقدير تجريبي لحجم الوصول" : "Illustrative estimate of reach" },
            { icon: TrendingUp, ar: "CTR متوسط 3.2%", en: "Avg. CTR 3.2%", sub: lang === "ar" ? "مؤشر تجريبي للأداء" : "Illustrative performance indicator" },
            { icon: ShieldCheck, ar: "إعلانات مدفوعة فقط", en: "Verified paid only", sub: lang === "ar" ? "لا منافسة مع إعلانات مجانية على نفس المساحة" : "No competition from free listings" },
          ].map((b, i) => (
            <div key={i} className="surface rounded-2xl p-6">
              <b.icon className="w-6 h-6 text-[#8a6f2e] mb-3" />
              <div className="font-display text-2xl mb-1 text-[#0a0a0a]">{lang === "ar" ? b.ar : b.en}</div>
              <div className="text-sm text-[#6b6b6b]">{b.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-[#0a0a0a]/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
          <div className="overline overline-gold mb-4">{lang === "ar" ? "كيف يعمل" : "How it works"}</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-12">{lang === "ar" ? "أربع خطوات. ثلاث دقائق." : "Four steps. Three minutes."}</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "01", ar: "أنشئ حساب", en: "Create an account", desc: lang === "ar" ? "حساب مجاني خلال 30 ثانية" : "Free account in 30s" },
              { n: "02", ar: "صمّم الإعلان", en: "Design your ad", desc: lang === "ar" ? "ارفع صورة + عنوان + رابط" : "Image + title + link" },
              { n: "03", ar: "اختر الموقع والمدة", en: "Pick placement & dates", desc: lang === "ar" ? "نحسب السعر تلقائياً" : "We calculate the price" },
              { n: "04", ar: "ادفع وانطلق", en: "Pay & go live", desc: lang === "ar" ? "بطاقة فيزا/ماستر — يظهر فوراً" : "Visa/Master — live instantly" },
            ].map((s) => (
              <div key={s.n} className="relative">
                <div className="font-display text-5xl text-[#c9a55c]/40 mb-2">{s.n}</div>
                <h3 className="font-display text-xl mb-2">{lang === "ar" ? s.ar : s.en}</h3>
                <p className="text-sm text-[#6b6b6b]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="overline overline-gold mb-4">{lang === "ar" ? "الأسعار الشفافة" : "Transparent pricing"}</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-3">{lang === "ar" ? "ادفع باليوم — بدون عقد طويل" : "Pay per day — no long-term contracts"}</h2>
        <p className="text-[#6b6b6b] mb-10 max-w-2xl">{lang === "ar" ? "السعر النهائي = السعر اليومي × عدد الأيام. الحد الأدنى 7 أيام." : "Final price = daily rate × days. Minimum 7 days."}</p>

        {pricing.length === 0 ? (
          <div className="text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pricing.map((p, i) => (
              <div key={p.placement} className={`surface rounded-2xl p-6 ${i === 0 ? "border-2 border-[#c9a55c]" : ""}`} data-testid={`pricing-${p.placement}`}>
                {i === 0 && <div className="overline overline-gold mb-2">{lang === "ar" ? "الأكثر مبيعاً" : "Most popular"}</div>}
                <h3 className="font-display text-xl mb-1">{lang === "ar" ? p.name_ar : p.name_en}</h3>
                <div className="my-4">
                  <span className="font-display text-4xl text-[#0a0a0a]">{formatSAR(p.price_per_day_sar)}</span>
                  <span className="text-sm text-[#6b6b6b] ms-2">{lang === "ar" ? "ريال/يوم" : "SAR/day"}</span>
                </div>
                <ul className="space-y-2 text-sm text-[#3a3a3a] mb-6">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "تتبع الظهور والنقرات" : "Track impressions & clicks"}</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "حد أدنى 7 أيام" : "Minimum 7 days"}</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "إيقاف/استئناف في أي وقت" : "Pause/resume anytime"}</li>
                </ul>
                <div className="text-xs text-[#6b6b6b] pt-4 border-t border-[#0a0a0a]/[0.06]">
                  {lang === "ar" ? `مثال: 7 أيام = ${formatSAR(p.price_per_day_sar * 7)} ريال · 30 يوم = ${formatSAR(p.price_per_day_sar * 30)} ريال` : `e.g., 7d = ${formatSAR(p.price_per_day_sar * 7)} · 30d = ${formatSAR(p.price_per_day_sar * 30)} SAR`}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Stats */}
      <section className="bg-[#0a0a0a] text-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
          <div className="grid sm:grid-cols-4 gap-8">
            {[
              // iter245 — sub descriptions clarify these are ILLUSTRATIVE
              // sample numbers (not verified results). Numbers themselves
              // unchanged; only the small caption under each stat is new.
              { v: "2M+", ar: "ظهور شهري", en: "monthly views", sub_ar: "تقدير تجريبي لحجم الوصول", sub_en: "Illustrative estimate of reach" },
              { v: "85K+", ar: "مستخدم نشط", en: "active users", sub_ar: "رقم تجريبي لأغراض العرض", sub_en: "Illustrative sample number" },
              { v: "13", ar: "مدينة سعودية", en: "Saudi cities", sub_ar: "ضمن نطاق العرض التجريبي", sub_en: "Within the illustrative scope" },
              { v: "3.2%", ar: "متوسط CTR", en: "avg CTR", sub_ar: "مؤشر تجريبي للأداء", sub_en: "Illustrative performance indicator" },
            ].map((s, i) => (
              <div key={i}>
                <div className="font-display text-5xl text-[#c9a55c] mb-2">{s.v}</div>
                <div className="text-sm text-[#c9a55c] uppercase tracking-widest font-semibold">{lang === "ar" ? s.ar : s.en}</div>
                <div className="text-xs text-[#8a8a8a] mt-1">{lang === "ar" ? s.sub_ar : s.sub_en}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 text-center">
        <Sparkles className="w-10 h-10 mx-auto text-[#c9a55c] mb-4" />
        <h2 className="font-display text-3xl sm:text-4xl mb-4">{lang === "ar" ? "جاهز لإطلاق حملتك؟" : "Ready to launch?"}</h2>
        <p className="text-[#6b6b6b] mb-8 max-w-xl mx-auto">{lang === "ar" ? "أنشئ حسابك المجاني وأطلق حملتك خلال 3 دقائق. ادفع آمن عبر ميسر (Moyasar)." : "Create a free account and launch in 3 minutes. Secure checkout via Moyasar."}</p>
        <button onClick={handleStart} className="btn-primary text-base mx-auto" data-testid="advertise-cta-bottom">
          <Zap className="w-4 h-4" /> {lang === "ar" ? "ابدأ الآن" : "Get started"} <ArrowRight className="w-4 h-4" />
        </button>
      </section>
    </div>
  );
}
